/**
 * WorkspacesManagementWorkspace - Manage workspace definitions
 *
 * Allows administrators to:
 * - View all workspaces in a sortable grid
 * - Drag to reorder (updates display_order)
 * - Rename workspace titles
 * - Edit workspace properties (icon, domain, description, access level)
 * - Create new workspaces
 * - Clone existing workspaces
 * - Toggle active/inactive
 * - Edit workspace JSON definition
 */

import React, { useState, useEffect, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Tooltip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  ContentCopy as CloneIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  PowerSettingsNew as ToggleIcon,
  DragIndicator as DragIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  Code as CodeIcon,
  DesignServices as DesignIcon,
  ViewQuilt as ViewQuiltIcon,
  TouchApp as TouchAppIcon,
  Widgets as WidgetsIcon,
  EditNote as EditNoteIcon,
  DashboardCustomize as DashboardIcon,
  Dashboard as WorkspaceIcon,
} from '@mui/icons-material';
import WorkspaceDesigner from './WorkspaceDesigner';
import { VisualWorkspaceDesigner } from './designer';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const getAccessToken = () => localStorage.getItem('waide_access_token');

const ICON_OPTIONS = [
  'ViewModule', 'MoveToInbox', 'Inventory', 'LocalShipping', 'Settings',
  'Dashboard', 'Assessment', 'Build', 'Storage', 'Category',
  'ShoppingCart', 'Assignment', 'Timeline', 'TrendingUp', 'Layers',
];

const DOMAIN_OPTIONS = ['WMS', 'FAST', 'INTERNAL'];
const ACCESS_LEVEL_OPTIONS = ['READ_ONLY', 'READ_WRITE', 'ADMIN'];

// ---------------------------------------------------------------------------
// Creation types — what users can create from the "+ New" button
// ---------------------------------------------------------------------------
const CREATION_TYPES = [
  {
    type: 'WORKSPACE',
    label: 'Workspace',
    description: 'A full workspace with views, grids, detail panels, and actions',
    icon: WorkspaceIcon,
    color: '#8b5cf6',
  },
  {
    type: 'VIEW',
    label: 'View',
    description: 'A reusable view tab that can be shared across workspaces',
    icon: ViewQuiltIcon,
    color: '#3b82f6',
  },
  {
    type: 'PICKER',
    label: 'Picker',
    description: 'A reusable entity picker overlay for selecting and assigning records',
    icon: TouchAppIcon,
    color: '#22c55e',
  },
  {
    type: 'PANEL',
    label: 'Panel',
    description: 'A reusable panel (detail grid, info card, etc.) for embedding in views',
    icon: WidgetsIcon,
    color: '#f59e0b',
  },
  {
    type: 'FORM',
    label: 'Form',
    description: 'A reusable form definition for create/edit wizards',
    icon: EditNoteIcon,
    color: '#06b6d4',
  },
  {
    type: 'WIZARD',
    label: 'Wizard',
    description: 'A multi-step wizard for guided workflows',
    icon: DashboardIcon,
    color: '#ec4899',
  },
];

// ---------------------------------------------------------------------------
// CreationHubDialog — "What do you want to create?"
// ---------------------------------------------------------------------------
function CreationHubDialog({ open, onClose, onSelect, colors }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: colors.surface,
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
        },
      }}
    >
      <DialogTitle sx={{ color: colors.textPrimary, pb: 0.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Create New
        </Typography>
        <Typography variant="caption" sx={{ color: colors.textMuted }}>
          Choose what you want to build. Workspaces are full screen layouts. Components are reusable
          pieces you can reference across workspaces with $ref.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 2,
            py: 1,
          }}
        >
          {CREATION_TYPES.map((ct) => {
            const Icon = ct.icon;
            return (
              <Box
                key={ct.type}
                onClick={() => onSelect(ct.type)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  p: 2.5,
                  borderRadius: 2,
                  border: `1px solid ${colors.border}`,
                  bgcolor: colors.background,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: ct.color,
                    bgcolor: `${ct.color}08`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${ct.color}20`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: `${ct.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon sx={{ fontSize: 24, color: ct.color }} />
                </Box>
                <Typography sx={{ color: colors.textPrimary, fontWeight: 700, fontSize: '0.9rem' }}>
                  {ct.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: colors.textMuted, textAlign: 'center', lineHeight: 1.3 }}
                >
                  {ct.description}
                </Typography>
                {ct.type !== 'WORKSPACE' && (
                  <Chip
                    label="Component"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      bgcolor: `${ct.color}15`,
                      color: ct.color,
                      mt: 0.5,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: colors.textMuted, textTransform: 'none' }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const WorkspacesManagementWorkspace = ({ colors }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(true);

  // Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [editingWorkspace, setEditingWorkspace] = useState(null);
  const [saving, setSaving] = useState(false);

  // Definition editor dialog
  const [defDialogOpen, setDefDialogOpen] = useState(false);
  const [defWorkspace, setDefWorkspace] = useState(null);
  const [defValue, setDefValue] = useState('');
  const [defError, setDefError] = useState(null);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState(null);

  // WYSIWYG Designer
  const [designerWorkspace, setDesignerWorkspace] = useState(null);
  // Visual Designer (new drag-and-drop)
  const [visualDesignerWorkspace, setVisualDesignerWorkspace] = useState(null);
  // Creation hub
  const [creationHubOpen, setCreationHubOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    workspaceCode: '',
    title: '',
    description: '',
    icon: 'ViewModule',
    domain: 'WMS',
    requiredAccessLevel: 'READ_ONLY',
    isActive: true,
  });

  const fetchWorkspaces = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/workspaces/manage?includeInactive=${includeInactive}`,
        { headers: { Authorization: `Bearer ${getAccessToken()}` } }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setWorkspaces(data);
    } catch (err) {
      setError(`Failed to load workspaces: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  // Auto-clear alerts
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(t);
    }
  }, [success]);

  const handleCreate = () => {
    setCreationHubOpen(true);
  };

  const handleCreationTypeSelect = (type) => {
    setCreationHubOpen(false);

    if (type === 'WORKSPACE') {
      // Open visual designer with a new blank workspace
      setVisualDesignerWorkspace({
        id: null, // null id = new workspace
        workspaceCode: '',
        title: 'New Workspace',
        icon: 'ViewModule',
        domain: 'WMS',
        requiredAccessLevel: 'READ_ONLY',
        isActive: true,
        definition: JSON.stringify({ title: 'New Workspace', icon: 'ViewModule', views: [] }),
      });
    } else {
      // Open visual designer in component creation mode
      // Pass a special flag indicating this is a new component, not a workspace
      setVisualDesignerWorkspace({
        id: null,
        workspaceCode: '',
        title: `New ${type.charAt(0) + type.slice(1).toLowerCase()}`,
        icon: 'Extension',
        domain: 'WMS',
        isActive: true,
        isComponent: true,
        componentType: type,
        definition: JSON.stringify({
          title: `New ${type.charAt(0) + type.slice(1).toLowerCase()}`,
          views: [],
        }),
      });
    }
  };

  const handleCreateWorkspaceLegacy = () => {
    setDialogMode('create');
    setEditingWorkspace(null);
    setFormData({
      workspaceCode: '',
      title: '',
      description: '',
      icon: 'ViewModule',
      domain: 'WMS',
      requiredAccessLevel: 'READ_ONLY',
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (ws) => {
    setDialogMode('edit');
    setEditingWorkspace(ws);
    setFormData({
      workspaceCode: ws.workspaceCode,
      title: ws.title,
      description: ws.description || '',
      icon: ws.icon || 'ViewModule',
      domain: ws.domain || 'WMS',
      requiredAccessLevel: ws.requiredAccessLevel || 'READ_ONLY',
      isActive: ws.isActive,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const url = dialogMode === 'create'
        ? `${API_BASE}/api/v1/workspaces/manage`
        : `${API_BASE}/api/v1/workspaces/manage/${editingWorkspace.id}`;
      const method = dialogMode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      setSuccess(dialogMode === 'create' ? 'Workspace created' : 'Workspace updated');
      setDialogOpen(false);
      fetchWorkspaces();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClone = async (ws) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/workspaces/manage/${ws.id}/clone`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }
      setSuccess(`Cloned "${ws.title}"`);
      fetchWorkspaces();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (ws) => {
    try {
      await fetch(`${API_BASE}/api/v1/workspaces/manage/${ws.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isActive: !ws.isActive }),
      });
      setSuccess(`${ws.title} ${ws.isActive ? 'deactivated' : 'activated'}`);
      fetchWorkspaces();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMove = async (ws, direction) => {
    const sorted = [...workspaces].sort((a, b) => a.displayOrder - b.displayOrder);
    const idx = sorted.findIndex((w) => w.id === ws.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    // Swap display orders
    const ids = sorted.map((w) => w.id);
    [ids[idx], ids[swapIdx]] = [ids[swapIdx], ids[idx]];

    try {
      await fetch(`${API_BASE}/api/v1/workspaces/manage/reorder`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids }),
      });
      fetchWorkspaces();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditDefinition = (ws) => {
    setDefWorkspace(ws);
    setDefError(null);
    try {
      const parsed = JSON.parse(ws.definition || '{}');
      setDefValue(JSON.stringify(parsed, null, 2));
    } catch {
      setDefValue(ws.definition || '{}');
    }
    setDefDialogOpen(true);
  };

  const handleSaveDefinition = async () => {
    // Validate JSON
    try {
      JSON.parse(defValue);
    } catch {
      setDefError('Invalid JSON');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/workspaces/manage/${defWorkspace.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ definition: defValue }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setSuccess('Definition saved');
      setDefDialogOpen(false);
      fetchWorkspaces();
    } catch (err) {
      setDefError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await fetch(`${API_BASE}/api/v1/workspaces/manage/${workspaceToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      });
      setSuccess(`Deleted "${workspaceToDelete.title}"`);
      setDeleteDialogOpen(false);
      setWorkspaceToDelete(null);
      fetchWorkspaces();
    } catch (err) {
      setError(err.message);
    }
  };

  const sorted = [...workspaces].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: colors.background, overflow: 'hidden', position: 'relative' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 3, py: 2, borderBottom: `1px solid ${colors.border}`,
      }}>
        <Box>
          <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
            Workspaces Management
          </Typography>
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            {workspaces.length} workspace{workspaces.length !== 1 ? 's' : ''}
            {' '}&middot;{' '}
            {workspaces.filter((w) => w.isActive).length} active
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                sx={{ '& .MuiSwitch-thumb': { bgcolor: '#8b5cf6' } }}
              />
            }
            label={<Typography variant="caption" sx={{ color: colors.textSecondary }}>Show Inactive</Typography>}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={fetchWorkspaces} size="small" sx={{ color: colors.textMuted }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            startIcon={<AddIcon />}
            onClick={handleCreate}
            variant="contained"
            size="small"
            sx={{
              bgcolor: '#8b5cf6', color: '#fff', textTransform: 'none',
              '&:hover': { bgcolor: '#7c3aed' },
            }}
          >
            New
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 3, mt: 1 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mx: 3, mt: 1 }}>
          {success}
        </Alert>
      )}

      {/* Table */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} sx={{ color: '#8b5cf6' }} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: colors.textMuted, fontWeight: 700, bgcolor: colors.surface, width: 60 }}>
                    Order
                  </TableCell>
                  <TableCell sx={{ color: colors.textMuted, fontWeight: 700, bgcolor: colors.surface }}>
                    Code
                  </TableCell>
                  <TableCell sx={{ color: colors.textMuted, fontWeight: 700, bgcolor: colors.surface }}>
                    Title
                  </TableCell>
                  <TableCell sx={{ color: colors.textMuted, fontWeight: 700, bgcolor: colors.surface }}>
                    Icon
                  </TableCell>
                  <TableCell sx={{ color: colors.textMuted, fontWeight: 700, bgcolor: colors.surface }}>
                    Domain
                  </TableCell>
                  <TableCell sx={{ color: colors.textMuted, fontWeight: 700, bgcolor: colors.surface }}>
                    Access
                  </TableCell>
                  <TableCell sx={{ color: colors.textMuted, fontWeight: 700, bgcolor: colors.surface, width: 80 }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ color: colors.textMuted, fontWeight: 700, bgcolor: colors.surface, width: 200 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((ws, idx) => (
                  <TableRow
                    key={ws.id}
                    sx={{
                      opacity: ws.isActive ? 1 : 0.5,
                      '&:hover': { bgcolor: colors.hoverBg || `${colors.surface}80` },
                    }}
                  >
                    {/* Order + Move buttons */}
                    <TableCell sx={{ color: colors.textSecondary }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <DragIcon sx={{ fontSize: 16, color: colors.textMuted, mr: 0.5 }} />
                        <Tooltip title="Move up">
                          <span>
                            <IconButton
                              size="small"
                              disabled={idx === 0}
                              onClick={() => handleMove(ws, 'up')}
                              sx={{ p: 0.25, color: colors.textMuted }}
                            >
                              <UpIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              size="small"
                              disabled={idx === sorted.length - 1}
                              onClick={() => handleMove(ws, 'down')}
                              sx={{ p: 0.25, color: colors.textMuted }}
                            >
                              <DownIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: colors.textSecondary, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {ws.workspaceCode}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                        {ws.title}
                      </Typography>
                      {ws.description && (
                        <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>
                          {ws.description.length > 60 ? ws.description.substring(0, 60) + '...' : ws.description}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                        {ws.icon || '-'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={ws.domain}
                        size="small"
                        sx={{
                          bgcolor: ws.domain === 'WMS' ? '#3b82f620' : '#8b5cf620',
                          color: ws.domain === 'WMS' ? '#3b82f6' : '#8b5cf6',
                          fontWeight: 600, fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                        {ws.requiredAccessLevel}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={ws.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: ws.isActive ? '#22c55e20' : '#ef444420',
                          color: ws.isActive ? '#22c55e' : '#ef4444',
                          fontWeight: 600, fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit properties">
                          <IconButton size="small" onClick={() => handleEdit(ws)} sx={{ color: '#3b82f6' }}>
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Visual Designer">
                          <IconButton size="small" onClick={() => setVisualDesignerWorkspace(ws)} sx={{ color: '#22c55e' }}>
                            <DesignIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Form Designer (legacy)">
                          <IconButton size="small" onClick={() => setDesignerWorkspace(ws)} sx={{ color: '#06b6d4' }}>
                            <DesignIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit JSON">
                          <IconButton size="small" onClick={() => handleEditDefinition(ws)} sx={{ color: '#f59e0b' }}>
                            <CodeIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Clone">
                          <IconButton size="small" onClick={() => handleClone(ws)} sx={{ color: '#8b5cf6' }}>
                            <CloneIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={ws.isActive ? 'Deactivate' : 'Activate'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleActive(ws)}
                            sx={{ color: ws.isActive ? '#ef4444' : '#22c55e' }}
                          >
                            <ToggleIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => { setWorkspaceToDelete(ws); setDeleteDialogOpen(true); }}
                            sx={{ color: colors.textMuted, '&:hover': { color: '#ef4444' } }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.surface, borderRadius: 1, border: `1px solid ${colors.border}` } }}
      >
        <DialogTitle sx={{ color: colors.textPrimary }}>
          {dialogMode === 'create' ? 'Create Workspace' : `Edit: ${editingWorkspace?.title}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Workspace Code"
              value={formData.workspaceCode}
              onChange={(e) => setFormData({ ...formData, workspaceCode: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })}
              disabled={dialogMode === 'edit'}
              size="small"
              fullWidth
              InputProps={{ sx: { color: colors.textPrimary } }}
              InputLabelProps={{ sx: { color: colors.textSecondary } }}
              helperText={dialogMode === 'create' ? 'Unique identifier (e.g., SHIPPING)' : undefined}
            />
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              size="small"
              fullWidth
              InputProps={{ sx: { color: colors.textPrimary } }}
              InputLabelProps={{ sx: { color: colors.textSecondary } }}
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              size="small"
              fullWidth
              multiline
              rows={2}
              InputProps={{ sx: { color: colors.textPrimary } }}
              InputLabelProps={{ sx: { color: colors.textSecondary } }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ color: colors.textSecondary }}>Icon</InputLabel>
              <Select
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                label="Icon"
                sx={{ color: colors.textPrimary }}
              >
                {ICON_OPTIONS.map((icon) => (
                  <MenuItem key={icon} value={icon}>{icon}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ color: colors.textSecondary }}>Domain</InputLabel>
              <Select
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                label="Domain"
                sx={{ color: colors.textPrimary }}
              >
                {DOMAIN_OPTIONS.map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ color: colors.textSecondary }}>Access Level</InputLabel>
              <Select
                value={formData.requiredAccessLevel}
                onChange={(e) => setFormData({ ...formData, requiredAccessLevel: e.target.value })}
                label="Access Level"
                sx={{ color: colors.textPrimary }}
              >
                {ACCESS_LEVEL_OPTIONS.map((a) => (
                  <MenuItem key={a} value={a}>{a}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
              sx={{ color: colors.textPrimary }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: colors.textMuted, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !formData.workspaceCode || !formData.title}
            variant="contained"
            sx={{ bgcolor: '#8b5cf6', textTransform: 'none', '&:hover': { bgcolor: '#7c3aed' } }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Definition Editor Dialog (Monaco) */}
      <Dialog
        open={defDialogOpen}
        onClose={() => setDefDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.surface, borderRadius: 1, border: `1px solid ${colors.border}`, height: '85vh' } }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <span>Definition: {defWorkspace?.title}</span>
          <Chip label={defWorkspace?.workspaceCode} size="small" sx={{ bgcolor: '#8b5cf620', color: '#8b5cf6' }} />
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
          {defError && <Alert severity="error" sx={{ mx: 2, mt: 1 }}>{defError}</Alert>}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Editor
              language="json"
              theme="vs-dark"
              value={defValue}
              onChange={(value) => { setDefValue(value || ''); setDefError(null); }}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                lineNumbers: 'on',
                folding: true,
                foldingStrategy: 'indentation',
                showFoldingControls: 'always',
                bracketPairColorization: { enabled: true },
                formatOnPaste: true,
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                tabSize: 2,
                automaticLayout: true,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: `1px solid ${colors.border}` }}>
          <Button onClick={() => setDefDialogOpen(false)} sx={{ color: colors.textMuted, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              try {
                const parsed = JSON.parse(defValue);
                setDefValue(JSON.stringify(parsed, null, 2));
                setDefError(null);
              } catch (e) {
                setDefError('Invalid JSON: ' + e.message);
              }
            }}
            sx={{ color: '#f59e0b', textTransform: 'none' }}
          >
            Format
          </Button>
          <Button
            onClick={handleSaveDefinition}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: '#8b5cf6', textTransform: 'none', '&:hover': { bgcolor: '#7c3aed' } }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save Definition'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { bgcolor: colors.surface, borderRadius: 1, border: `1px solid ${colors.border}` } }}
      >
        <DialogTitle sx={{ color: colors.textPrimary }}>Delete Workspace</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: colors.textSecondary }}>
            Are you sure you want to delete <strong>{workspaceToDelete?.title}</strong> ({workspaceToDelete?.workspaceCode})?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: colors.textMuted, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" sx={{ textTransform: 'none' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Visual Workspace Designer (full overlay — new drag-and-drop) */}
      {visualDesignerWorkspace && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 25,
            bgcolor: colors.background,
          }}
        >
          <VisualWorkspaceDesigner
            workspace={visualDesignerWorkspace}
            onSave={() => {
              fetchWorkspaces();
              setSuccess('Workspace definition saved');
            }}
            onClose={() => setVisualDesignerWorkspace(null)}
            colors={colors}
          />
        </Box>
      )}

      {/* Form-based Workspace Designer (full overlay — legacy) */}
      {designerWorkspace && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 20,
            bgcolor: colors.background,
          }}
        >
          <WorkspaceDesigner
            workspace={designerWorkspace}
            onSave={() => {
              fetchWorkspaces();
              setSuccess('Workspace definition saved');
            }}
            onClose={() => setDesignerWorkspace(null)}
            colors={colors}
          />
        </Box>
      )}

      {/* Creation Hub Dialog */}
      <CreationHubDialog
        open={creationHubOpen}
        onClose={() => setCreationHubOpen(false)}
        onSelect={handleCreationTypeSelect}
        colors={colors}
      />
    </Box>
  );
};

export default WorkspacesManagementWorkspace;
